// Board.js — S5 board: block list (drag source) + four list columns.
// Drop on a column adds the account to that list (createdAt = now).
// Every block also has a keyboard-accessible quick-add select so drag is
// an enhancement, not the only path.

import React, { useState } from 'react';
import { LIST_DEFS } from '../lib/lists';

const BLOCK_DATA = 'text/plain';

// Per-list enforcement toggle (S6): mute (private client pref) or block
// (public listblock record). The eternity column keeps the toggle too —
// enforcement applies to all members regardless of duration, so a
// permanent block list is a legitimate use.

function ModeToggle({ mode, onChange, disabled }) {
  const set = (next) => {
    // Clicking the ACTIVE button again unsubscribes (removes enforcement).
    onChange(next === mode ? null : next);
  };

  return (
    <div className="mode-toggle" role="group" aria-label="List enforcement">
      <button
        type="button"
        className={mode === 'mute' ? 'active' : ''}
        aria-pressed={mode === 'mute'}
        onClick={() => set('mute')}
        disabled={disabled}
      >
        {mode === 'mute' ? '\u2713 Mute' : 'Mute'}
      </button>
      <button
        type="button"
        className={mode === 'block' ? 'active' : ''}
        aria-pressed={mode === 'block'}
        onClick={() => set('block')}
        disabled={disabled}
      >
        {mode === 'block' ? '\u2713 Block' : 'Block'}
      </button>
      {mode && (
        <button
          type="button"
          className="mode-clear"
          aria-label="No enforcement"
          onClick={() => set(null)}
          disabled={disabled}
        >
          None
        </button>
      )}
    </div>
  );
}

function ProfileLine({ profile }) {
  const label = profile.displayName || profile.handle || profile.did;

  return (
    <span className="profile-line">
      {profile.avatar ? (
        <img
          className="avatar"
          src={profile.avatar}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="avatar avatar-placeholder" aria-hidden="true" />
      )}
      <span className="profile-text">
        <span className="profile-name">{label}</span>
        {profile.handle && profile.handle !== label ? (
          <span className="profile-handle">{profile.handle}</span>
        ) : null}
      </span>
    </span>
  );
}

function BlockRow({ block, onAdd }) {
  const [adding, setAdding] = useState('');

  const quickAdd = (e) => {
    const listName = e.target.value;

    if (!listName) return;
    setAdding(listName);
    // Defer so the select can reset after the handler.
    window.setTimeout(() => onAdd(block, listName), 0);
    e.target.value = '';
  };

  return (
    <li
      className="block-row"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(BLOCK_DATA, block.did);
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      <ProfileLine profile={block} />
      <select
        className="quick-add"
        aria-label={`Add ${block.handle || block.did} to a list`}
        value={adding || ''}
        onChange={quickAdd}
      >
        <option value="">Add to...</option>
        {LIST_DEFS.map((def) => (
          <option key={def.name} value={def.name}>
            {def.name}
          </option>
        ))}
      </select>
    </li>
  );
}

function ItemRow({ item, listName, onRemove, busy }) {
  const [confirming, setConfirming] = useState(false);

  const handleClick = () => {
    if (!confirming) {
      setConfirming(true);
      window.setTimeout(() => setConfirming(false), 2500);

      return;
    }

    setConfirming(false);
    onRemove(item, listName);
  };

  return (
    <li className="item-row">
      <ProfileLine profile={item.subject} />
      <button
        type="button"
        className={`remove-btn${confirming ? ' confirming' : ''}`}
        onClick={handleClick}
        disabled={busy}
        aria-label={
          confirming
            ? `Confirm remove ${item.subject?.handle || item.subject?.did || ''} from ${listName}`
            : `Remove ${item.subject?.handle || item.subject?.did || ''} from ${listName}`
        }
      >
        {confirming ? 'Confirm' : String.fromCharCode(0xd7)}
      </button>
    </li>
  );
}

const Board = ({ blocks, columns, modes, onAdd, onModeChange, onRemove, busy }) => {
  const [dragOverUri, setDragOverUri] = useState(null);

  const handleDrop = (listName, e) => {
    e.preventDefault();
    setDragOverUri(null);
    const did = e.dataTransfer.getData(BLOCK_DATA);

    if (!did) return;
    const block = blocks.find((b) => b.did === did);

    if (!block) return;
    onAdd(block, listName);
  };

  return (
    <div className="board">
      <section className="blocks-pane" aria-label="Your blocks — drag into a list">
        <h2>
          Blocks <span className="count">{blocks.length}</span>
        </h2>
        {blocks.length === 0 ? (
          <p className="muted">No blocks to sort.</p>
        ) : (
          <ul className="block-list">
            {blocks.map((block) => (
              <BlockRow key={block.did} block={block} onAdd={onAdd} />
            ))}
          </ul>
        )}
      </section>

      <section className="columns" aria-label="Your timer lists">
        {columns.map((col) => {
          const droppable = dragOverUri === col.uri;

          return (
            <div
              key={col.uri}
              className={`column${droppable ? ' drag-over' : ''}`}
              aria-label={`${col.name} list`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';

                if (dragOverUri !== col.uri) setDragOverUri(col.uri);
              }}
              onDragLeave={() => setDragOverUri((u) => (u === col.uri ? null : u))}
              onDrop={(e) => handleDrop(col.name, e)}
            >
              <h3 className="column-title">
                {col.name} <span className="count">{col.items.length}</span>
                <span
                  className={`enforcement-chip ${modes[col.name] ?? 'none'}`}
                  title={
                    modes[col.name]
                      ? `List ${modes[col.name] === 'mute' ? 'muted' : 'blocked'} — all members affected`
                      : 'No enforcement — list membership only (no mute/block applied)'
                  }
                >
                  {modes[col.name] === 'mute'
                    ? 'Muted'
                    : modes[col.name] === 'block'
                      ? 'Blocked'
                      : 'Unsubscribed'}
                </span>
              </h3>
              <ModeToggle
                mode={modes[col.name] ?? null}
                onChange={(next) => onModeChange(col, next)}
                disabled={busy}
              />
              <ul className="column-items">
                {col.items.map((item) => (
                  <ItemRow
                    key={item.uri}
                    item={item}
                    listName={col.name}
                    onRemove={onRemove}
                    busy={busy}
                  />
                ))}
                {col.items.length === 0 && (
                  <li className="muted empty">Drop blocks here</li>
                )}
              </ul>
            </div>
          );
        })}
      </section>
      {busy && <p className="status-line">Working...</p>}
    </div>
  );
};

export default Board;
